import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Upload, Play, Save, FileDown, Plus, RefreshCw, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import PanelState, { extractApiError } from "@/components/PanelState";

/**
 * Tech Pay Statement (Preliminary) — pt.86 (2026-09-17).
 * PRELIMINARY, informational. Admin-only. XLSX-first ingestion.
 * Every screen carries the mandatory disclaimer.
 */
export default function TechPayStatements() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const tenantQ = `tenant_slug=${slug}`;

  // ---- period state ----
  const [periodStart, setPeriodStart] = useState("");
  const [techFilter, setTechFilter] = useState("");
  const [buFilter, setBuFilter] = useState("");

  // ---- data state ----
  const [rates, setRates] = useState({ current: [], history: [] });
  const [settings, setSettings] = useState(null);
  const [sources, setSources] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [computeResult, setComputeResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [computing, setComputing] = useState(false);
  const [error, setError] = useState(null);

  const paymentsRef = useRef();
  const invoiceItemsRef = useRef();
  const jobSplitsRef = useRef();

  // -------- initial load
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { data: dp } = await api.get(`/tech-pay-statement/default-period?${tenantQ}`);
        setPeriodStart(dp.period_start);
        const [{ data: r }, { data: s }, { data: sn }] = await Promise.all([
          api.get(`/tech-pay-statement/rates?${tenantQ}`),
          api.get(`/tech-pay-statement/settings?${tenantQ}`),
          api.get(`/tech-pay-statement/snapshots?${tenantQ}`),
        ]);
        setRates(r);
        setSettings(s);
        setSnapshots(sn);
      } catch (e) {
        setError(extractApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // -------- reload period-scoped sources when period changes
  useEffect(() => {
    if (!periodStart) return;
    (async () => {
      try {
        const { data } = await api.get(`/tech-pay-statement/period/${periodStart}/sources?${tenantQ}`);
        setSources(data);
      } catch (e) {
        setSources([]);
      }
    })();
  }, [periodStart, slug]);

  const hasSource = (kind) => sources.some((s) => s.kind === kind);

  const onUpload = async (file, kind) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(
        `/tech-pay-statement/upload/${kind}?${tenantQ}&period_start=${periodStart}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(`${kind} uploaded — ${data.row_count} rows`);
      const { data: fresh } = await api.get(`/tech-pay-statement/period/${periodStart}/sources?${tenantQ}`);
      setSources(fresh);
    } catch (e) {
      toast.error(`Upload failed: ${extractApiError(e)}`);
    }
  };

  const onCompute = async () => {
    setComputing(true);
    try {
      const { data } = await api.post(
        `/tech-pay-statement/compute?${tenantQ}`,
        { period_start: periodStart, tech_filter: techFilter || null, bu_filter: buFilter || null }
      );
      setComputeResult(data);
      toast.success(`Computed ${data.statements?.length ?? 0} tech statement(s)`);
    } catch (e) {
      toast.error(`Compute failed: ${extractApiError(e)}`);
    } finally {
      setComputing(false);
    }
  };

  const onSaveSnapshot = async () => {
    try {
      const { data } = await api.post(
        `/tech-pay-statement/snapshots?${tenantQ}`,
        { period_start: periodStart, tech_filter: techFilter || null, bu_filter: buFilter || null }
      );
      toast.success(`Snapshot saved (${data.id.slice(0, 8)}…)`);
      const { data: fresh } = await api.get(`/tech-pay-statement/snapshots?${tenantQ}`);
      setSnapshots(fresh);
    } catch (e) {
      toast.error(`Save failed: ${extractApiError(e)}`);
    }
  };

  const onDownloadPdf = async (kind, techKey = null) => {
    const url = kind === "all" ? "/tech-pay-statement/pdf/all" : "/tech-pay-statement/pdf/tech";
    try {
      const resp = await api.post(
        `${url}?${tenantQ}`,
        { period_start: periodStart, tech_filter: techKey },
        { responseType: "blob" }
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const dl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl;
      a.download = `tech-pay-${techKey || "all"}-${periodStart}.pdf`;
      a.click();
      window.URL.revokeObjectURL(dl);
    } catch (e) {
      toast.error(`PDF download failed: ${extractApiError(e)}`);
    }
  };

  if (loading) return <PanelState kind="loading" data-testid="tps-loading" />;
  if (error) return <PanelState kind="error" message={error} />;

  return (
    <div className="max-w-[1400px] mx-auto p-4 space-y-4" data-testid="tech-pay-statements-page">
      {/* Header */}
      <div className="flex items-center gap-3 text-[13px]">
        <button
          onClick={() => navigate(`/${slug}/app/comp`)}
          className="flex items-center gap-1 text-[color:var(--cc-ink-muted)] hover:text-[#0e1420]"
          data-testid="tps-back-to-comp"
        >
          <ChevronLeft size={14} /> Back to Comp
        </button>
      </div>
      <div>
        <h1 className="font-display font-bold text-[28px] text-[#0e1420]">Tech Pay Statements</h1>
        <div className="mt-1 inline-block bg-[#fef3d9] text-[#8a5a1a] px-2 py-0.5 rounded font-semibold text-[11px] uppercase tracking-wider" data-testid="tps-preliminary-banner">
          PRELIMINARY — not payroll of record
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white border border-[#e7e9ee] rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[color:var(--cc-ink-muted)] font-semibold block mb-1">Pay period start (Sunday)</label>
          <input
            type="date" value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
            className="border border-[#cfd4dc] rounded px-2 py-1.5 text-[13px] w-full"
            data-testid="tps-period-start-input"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[color:var(--cc-ink-muted)] font-semibold block mb-1">Tech filter (optional)</label>
          <input
            type="text" value={techFilter}
            onChange={(e) => setTechFilter(e.target.value)}
            placeholder="e.g., Joey Byers"
            className="border border-[#cfd4dc] rounded px-2 py-1.5 text-[13px] w-full"
            data-testid="tps-tech-filter-input"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[color:var(--cc-ink-muted)] font-semibold block mb-1">Business unit filter</label>
          <input
            type="text" value={buFilter}
            onChange={(e) => setBuFilter(e.target.value)}
            placeholder="e.g., Residential"
            className="border border-[#cfd4dc] rounded px-2 py-1.5 text-[13px] w-full"
            data-testid="tps-bu-filter-input"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={onCompute}
            disabled={computing || !periodStart}
            className="bg-[#0e1420] text-white px-4 py-2 rounded text-[13px] font-semibold w-full flex items-center justify-center gap-2 disabled:bg-[#98a0ad]"
            data-testid="tps-compute-btn"
          >
            {computing ? <RefreshCw size={14} className="animate-spin" /> : <Play size={14} />}
            {computing ? "Computing…" : "Compute"}
          </button>
        </div>
      </div>

      {/* Uploads */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          { kind: "st_payments", label: "ST Payments Journal", ref: paymentsRef, help: "Payments Journal / AR Payments Received export" },
          { kind: "st_invoice_items", label: "ST Invoice Item Detail", ref: invoiceItemsRef, help: "Same as st_commission — invoice-line-level export" },
          { kind: "st_job_splits", label: "ST Job Splits (optional)", ref: jobSplitsRef, help: "Missing → 100% to primary technician" },
        ].map(({ kind, label, ref, help }) => (
          <div key={kind} className="bg-white border border-[#e7e9ee] rounded-xl p-4" data-testid={`tps-upload-${kind}`}>
            <div className="font-display font-semibold text-[14px] mb-1 flex items-center gap-2">
              <Upload size={14} /> {label}
            </div>
            <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mb-2">{help}</div>
            <div
              className="border-2 border-dashed border-[#cfd4dc] rounded-lg p-4 text-center cursor-pointer hover:bg-[#f5f6f8]"
              onClick={() => ref.current?.click()}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); onUpload(e.dataTransfer.files?.[0], kind); }}
            >
              <div className="text-[12.5px] font-semibold">
                {hasSource(kind) ? "Replace file" : "Drop .xlsx or .csv"}
              </div>
              <input
                ref={ref} type="file" accept=".xlsx,.csv"
                data-testid={`tps-upload-${kind}-input`}
                onChange={(e) => onUpload(e.target.files?.[0], kind)}
                className="hidden"
              />
              {hasSource(kind) && (
                <div className="text-[11px] text-[#0e8a68] mt-1 font-semibold" data-testid={`tps-upload-${kind}-ok`}>
                  ✓ on file ({sources.find((s) => s.kind === kind)?.row_count ?? 0} rows)
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Settings + Rates side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <SettingsCard settings={settings} tenantQ={tenantQ} onUpdated={setSettings} />
        <RatesCard rates={rates} tenantQ={tenantQ} onUpdated={setRates} />
      </div>

      {/* Compute Result */}
      {computeResult && (
        <ComputeResultView
          result={computeResult}
          onSaveSnapshot={onSaveSnapshot}
          onDownloadPdf={onDownloadPdf}
        />
      )}

      {/* Snapshots */}
      {snapshots.length > 0 && (
        <div className="bg-white border border-[#e7e9ee] rounded-xl p-4">
          <div className="font-display font-semibold text-[14px] mb-2">Saved Snapshots</div>
          <table className="w-full text-[12.5px]">
            <thead className="bg-[#f5f6f8] text-[11px] uppercase tracking-wider text-[color:var(--cc-ink-muted)]">
              <tr>
                <th className="text-left py-2 px-3">Period</th>
                <th className="text-left py-2 px-3">Saved</th>
                <th className="text-left py-2 px-3">By</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {snapshots.slice(0, 20).map((s) => (
                <tr key={s.id} className="border-t border-[#eef1f6]" data-testid={`tps-snapshot-row-${s.id.slice(0, 8)}`}>
                  <td className="py-2 px-3 font-mono-data">{s.period_start} – {s.period_end}</td>
                  <td className="py-2 px-3 text-[11.5px]">{new Date(s.saved_at).toLocaleString()}</td>
                  <td className="py-2 px-3 text-[11.5px]">{s.saved_by}</td>
                  <td className="text-right py-2 px-3"></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SettingsCard({ settings, tenantQ, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const patch = async (update) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/tech-pay-statement/settings?${tenantQ}`, update);
      onUpdated(data);
      toast.success("Settings updated");
    } catch (e) {
      toast.error(extractApiError(e));
    } finally { setBusy(false); }
  };
  if (!settings) return null;
  return (
    <div className="bg-white border border-[#e7e9ee] rounded-xl p-4" data-testid="tps-settings-card">
      <div className="font-display font-semibold text-[14px] mb-2">Compute Settings</div>
      <div className="space-y-2 text-[13px]">
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-[color:var(--cc-ink-muted)] font-semibold mb-1">Commission basis</label>
          <select
            value={settings.commission_basis}
            onChange={(e) => patch({ commission_basis: e.target.value })}
            disabled={busy}
            className="border border-[#cfd4dc] rounded px-2 py-1.5 text-[13px] w-full"
            data-testid="tps-commission-basis-select"
          >
            <option value="collected_revenue">collected_revenue (default)</option>
            <option value="collected_revenue_less_materials">collected_revenue_less_materials (pending type verification)</option>
          </select>
        </div>
        <div>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!settings.commercial_accrual_enabled}
              onChange={(e) => patch({ commercial_accrual_enabled: e.target.checked })}
              disabled={busy}
              data-testid="tps-commercial-accrual-toggle"
            />
            <span className="text-[13px]">commercial_accrual_enabled (no-op — logs warning if true)</span>
          </label>
          <div className="text-[11px] text-[color:var(--cc-ink-muted)] mt-1">
            Note: {settings.commercial_accrual_notes || "Brett default, Nathanael pending"}
          </div>
        </div>
      </div>
    </div>
  );
}

function RatesCard({ rates, tenantQ, onUpdated }) {
  const [name, setName] = useState("");
  const [pct, setPct] = useState("");
  const [effective, setEffective] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!name || !pct || !effective) {
      toast.error("Name, rate, and effective date all required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/tech-pay-statement/rates?${tenantQ}`, {
        tech_key: name.trim().toLowerCase(),
        tech_name: name.trim(),
        rate_pct: pct,
        effective_date: effective,
      });
      const { data: fresh } = await api.get(`/tech-pay-statement/rates?${tenantQ}`);
      onUpdated(fresh);
      setName(""); setPct(""); setEffective("");
      toast.success("Rate added");
    } catch (e) {
      toast.error(extractApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-[#e7e9ee] rounded-xl p-4" data-testid="tps-rates-card">
      <div className="font-display font-semibold text-[14px] mb-2">Commission Rate Table</div>
      <div className="grid grid-cols-4 gap-2 mb-2">
        <input placeholder="Tech name" value={name} onChange={(e) => setName(e.target.value)} className="border border-[#cfd4dc] rounded px-2 py-1 text-[12.5px]" data-testid="tps-rate-name-input" />
        <input placeholder="Rate (0.185)" value={pct} onChange={(e) => setPct(e.target.value)} className="border border-[#cfd4dc] rounded px-2 py-1 text-[12.5px]" data-testid="tps-rate-pct-input" />
        <input type="date" value={effective} onChange={(e) => setEffective(e.target.value)} className="border border-[#cfd4dc] rounded px-2 py-1 text-[12.5px]" data-testid="tps-rate-effective-input" />
        <button onClick={add} disabled={busy} className="bg-[#0e1420] text-white rounded px-2 py-1 text-[12.5px] font-semibold flex items-center gap-1 justify-center" data-testid="tps-rate-add-btn">
          <Plus size={12} /> Add
        </button>
      </div>
      <table className="w-full text-[12.5px]">
        <thead className="bg-[#f5f6f8] text-[10.5px] uppercase tracking-wider text-[color:var(--cc-ink-muted)]">
          <tr><th className="text-left py-1.5 px-2">Tech</th><th className="text-right py-1.5 px-2">Rate</th><th className="text-left py-1.5 px-2">Since</th></tr>
        </thead>
        <tbody>
          {rates.current.map((r) => (
            <tr key={`${r.tech_key}-${r.effective_date}`} className="border-t border-[#eef1f6]" data-testid={`tps-rate-row-${r.tech_key}`}>
              <td className="py-1.5 px-2">{r.tech_name}</td>
              <td className="py-1.5 px-2 text-right font-mono-data">{(r.rate_pct * 100).toFixed(1)}%</td>
              <td className="py-1.5 px-2 text-[11.5px]">{r.effective_date}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rates.current.length === 0 && (
        <div className="text-[12px] text-[color:var(--cc-ink-muted)] mt-2 italic">No rates configured yet. Techs without a rate will show "RATE NOT SET" and be excluded from totals.</div>
      )}
    </div>
  );
}

function ComputeResultView({ result, onSaveSnapshot, onDownloadPdf }) {
  const money = (v) => {
    if (v === "RATE NOT SET" || v === null || v === undefined) return v || "—";
    const n = Number(v);
    return isNaN(n) ? String(v) : `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const pct = (v) => v == null ? "—" : `${(Number(v) * 100).toFixed(1)}%`;

  return (
    <div className="space-y-3" data-testid="tps-compute-result">
      {/* Run-level flags */}
      {result.run_flags?.length > 0 && (
        <div className="bg-[#fff7ed] border border-[#f59e0b] rounded-xl p-3" data-testid="tps-run-flags">
          <div className="text-[11.5px] uppercase font-semibold text-[#7c2d12] mb-1">Run-level flags</div>
          {result.run_flags.map((f, i) => (
            <div key={i} className="text-[12px] text-[#7c2d12]">• <span className="font-semibold">{f.code}</span>: {f.detail}</div>
          ))}
        </div>
      )}

      {/* Action bar */}
      <div className="flex gap-2 justify-end">
        <button onClick={onSaveSnapshot} className="bg-white border border-[#cfd4dc] rounded px-3 py-1.5 text-[12.5px] font-semibold flex items-center gap-1" data-testid="tps-save-snapshot-btn">
          <Save size={12} /> Save Snapshot
        </button>
        <button onClick={() => onDownloadPdf("all")} className="bg-[#0e1420] text-white rounded px-3 py-1.5 text-[12.5px] font-semibold flex items-center gap-1" data-testid="tps-pdf-all-btn">
          <FileDown size={12} /> Export All (PDF)
        </button>
      </div>

      {/* Per-tech cards */}
      {result.statements.map((stmt) => (
        <div key={stmt.tech_key} className="bg-white border border-[#e7e9ee] rounded-xl p-4" data-testid={`tps-tech-card-${stmt.tech_key.replace(/\s+/g, "-")}`}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-display font-bold text-[16px]">{stmt.tech_name}</div>
              {stmt.rate_status !== "OK" && (
                <div className="text-[11px] bg-[#fee2e2] text-[#7f1d1d] px-1.5 py-0.5 rounded inline-block font-semibold mt-1" data-testid={`tps-tech-rate-not-set-${stmt.tech_key.replace(/\s+/g, "-")}`}>
                  RATE NOT SET
                </div>
              )}
            </div>
            <button onClick={() => onDownloadPdf("tech", stmt.tech_key)} className="text-[11.5px] text-[#0e1420] underline hover:no-underline" data-testid={`tps-tech-pdf-${stmt.tech_key.replace(/\s+/g, "-")}`}>
              PDF
            </button>
          </div>
          {/* Summary */}
          <div className="grid grid-cols-5 gap-2 mb-3 bg-[#f5f6f8] rounded p-3 text-center">
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-[color:var(--cc-ink-muted)]">Collected</div>
              <div className="font-mono-data font-semibold text-[13px]">{money(stmt.summary.total_collected)}</div>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-[color:var(--cc-ink-muted)]">Materials</div>
              <div className="font-mono-data font-semibold text-[13px]">{money(stmt.summary.materials_share_total)}</div>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-[color:var(--cc-ink-muted)]">Base</div>
              <div className="font-mono-data font-semibold text-[13px]">{money(stmt.summary.commission_base)}</div>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-[color:var(--cc-ink-muted)]">Blended</div>
              <div className="font-mono-data font-semibold text-[13px]">{pct(stmt.summary.blended_rate)}</div>
            </div>
            <div className="bg-[#c99a3f] rounded px-2 py-1 -m-1">
              <div className="text-[10.5px] uppercase tracking-wider text-white">Commission</div>
              <div className="font-mono-data font-bold text-[14px] text-white" data-testid={`tps-tech-commission-${stmt.tech_key.replace(/\s+/g, "-")}`}>
                {money(stmt.summary.commission_earned)}
              </div>
            </div>
          </div>
          {/* Section tables */}
          {[
            { key: "A", label: "A · Completed jobs" },
            { key: "B", label: "B · Open jobs / advances" },
            { key: "C", label: "C · Adjustments" },
          ].map(({ key, label }) => stmt.sections[key]?.length ? (
            <div key={key} className="mb-3">
              <div className="text-[12px] font-semibold text-[#0e1f3f] mb-1">{label}</div>
              <div className="overflow-x-auto">
                <table className="w-full text-[11.5px]">
                  <thead className="bg-[#f5f6f8] text-[9.5px] uppercase tracking-wider text-[color:var(--cc-ink-muted)]">
                    <tr>
                      <th className="text-left py-1 px-2">Job #</th>
                      <th className="text-left py-1 px-2">Customer</th>
                      <th className="text-left py-1 px-2">BU</th>
                      <th className="text-left py-1 px-2">Status</th>
                      <th className="text-right py-1 px-2">Inv Total</th>
                      <th className="text-right py-1 px-2">Collected</th>
                      <th className="text-right py-1 px-2">Split</th>
                      <th className="text-right py-1 px-2">Tech Coll</th>
                      <th className="text-right py-1 px-2">Materials</th>
                      <th className="text-right py-1 px-2">Commission</th>
                      <th className="text-left py-1 px-2">Flags</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stmt.sections[key].map((ln, i) => (
                      <tr key={`${ln.job_number}-${i}`} className="border-t border-[#eef1f6]" data-testid={`tps-line-${stmt.tech_key.replace(/\s+/g, "-")}-${ln.job_number}`}>
                        <td className="py-1 px-2 font-mono-data">{ln.job_number}</td>
                        <td className="py-1 px-2">{ln.customer_name}</td>
                        <td className="py-1 px-2 text-[10px]">{ln.business_unit}</td>
                        <td className="py-1 px-2 text-[10px]">{ln.status}</td>
                        <td className="py-1 px-2 text-right font-mono-data">{money(ln.invoice_total)}</td>
                        <td className="py-1 px-2 text-right font-mono-data">{money(ln.collected_this_period)}</td>
                        <td className="py-1 px-2 text-right font-mono-data">{pct(ln.tech_split_pct)}</td>
                        <td className="py-1 px-2 text-right font-mono-data">{money(ln.tech_collected)}</td>
                        <td className="py-1 px-2 text-right font-mono-data">{money(ln.materials_share)}</td>
                        <td className={`py-1 px-2 text-right font-mono-data font-semibold ${ln.commission === "RATE NOT SET" ? "text-[#7f1d1d]" : ""}`}>
                          {ln.commission === "RATE NOT SET" ? ln.commission : money(ln.commission)}
                        </td>
                        <td className="py-1 px-2">
                          <div className="flex flex-wrap gap-1">
                            {ln.flags?.map((f, j) => (
                              <span key={j} title={f.detail} className="bg-[#fef3d9] text-[#8a5a1a] px-1 py-0.5 rounded text-[9px] font-semibold">{f.code}</span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null)}
        </div>
      ))}
    </div>
  );
}
